<template>
  <div class="import-url-group-modal">
    <div v-if="!csvLoaded">
      <div v-if="csvLoading" class="text-center">
        <div class="info-section d-flex w-100 flex-column justify-space-between text-center">
          <div class="csv-upload-report d-flex w-100 pl-4 pt-4 pr-4 pb-4">
            <v-icon class="file-icon mr-2" size="70" icon="$dataNoHexagon" />
            <div class="d-flex flex-column align-start justify-center w-100">
              <div class="subtitle2">{{ file?.name }}</div>
              <div class="body3 text-indigo-medium mb-2">{{ humanizeFileSize(file?.size) }}</div>
              <v-progress-linear
                color="green-base"
                bg-color="indigo-pale"
                rounded
                height="4"
                indeterminate
              />
            </div>
          </div>
          <v-btn
            rounded
            class="align-self-end"
            color="primary"
            data-testid="modal-action-button"
            size="large"
            @click="$emit('dismissed')"
          >
            {{ $t("general.cancel") }}
          </v-btn>
        </div>
      </div>
      <template v-else>
        <v-form ref="form" v-model="valid" @submit.prevent>
          <draggable-file-uploader
            accept="text/plain"
            :description="$t(`modals.importUrlGroupAllowBlockListFromCsv.uploadTxt`)"
            :subtitle="$t(`modals.importUrlGroupAllowBlockListFromCsv.uploadTextSubtitle`)"
            :class="{ 'error-border': !!errorMessage }"
            :errorMessage="errorMessage"
            @file-changed="handleFileUpload"
          />
          <div class="caption mt-2">
            <a
              data-testid="download-template-link"
              class="coro-link"
              @click="getSwgExampleFile"
              v-text="$t(`modals.importUrlGroupAllowBlockListFromCsv.downloadTemplate`)"
            />
            &nbsp;{{ $t("modals.importUrlGroupAllowBlockListFromCsv.downloadTemplateInfo") }}
            {{ $t(`modals.importUrlGroupAllowBlockListFromCsv.disclaimer`) }}
          </div>
          <v-text-field
            v-model="localValue.nickname"
            outlined
            background-color="white"
            hide-details="auto"
            :error-messages="nicknameError"
            :counter="100"
            :placeholder="$t('modals.allowBlockListSwg.nicknamePlaceholder')"
            :label="$t('modals.allowBlockListSwg.nameLabel')"
            :aria-label="$t('modals.allowBlockListSwg.nameLabel')"
            class="mt-6 nickname-input"
            @input="clearNicknameError"
          >
            <template v-slot:message="{ message }">
              <div class="d-flex align-center">
                <v-icon icon="icon-warning-xs" color="red-faint" size="18" class="mr-1" />
                <span>{{ message }}</span>
              </div>
            </template>
          </v-text-field>
          <div class="mt-6">
            <div>
              <v-autocomplete
                v-model="localValue.labels"
                class="mt-4 labels-autocomplete"
                :items="labels"
                :label="$t('network.secureWebGateway.labelsAutocompletePlaceholder')"
                :placeholder="$t('network.secureWebGateway.labelsAutocompletePlaceholder')"
                :disabled="actionNotAllowed"
                :error-messages="labelError"
                variant="outlined"
                multiple
                return-object
                chips
                closable-chips
                clear-on-select
                data-testid="device-settings-tamper-resistance-labels"
                item-value="id"
                item-title="name"
                @update:model-value="clearLabelError"
              >
                <template v-slot:message="{ message }">
                  <div class="d-flex align-center">
                    <v-icon icon="icon-warning-xs" color="red-faint" size="18" class="mr-1" />
                    <span>{{ message }}</span>
                  </div>
                </template>
                <template v-slot:chip="{ props, item }">
                  <v-chip
                    v-bind="props"
                    size="default"
                    :color="item.raw.color"
                    close-icon="$close"
                    :text="item.raw.name"
                    class="label-chip"
                    variant="flat"
                    label
                    rounded="3"
                  >
                    <span class="coro-label text-uppercase text-primary mr-1" v-text="item.title">
                    </span>
                  </v-chip>
                </template>
                <template v-slot:item="{ item, props }">
                  <v-list-item v-bind="props" title="">
                    <template #default>
                      <div class="d-flex align-center label-popup-block">
                        <div
                          class="color-preview mr-2"
                          :style="{ 'background-color': item.raw.color }"
                        />
                        <span v-text="item.raw.name" />
                      </div>
                    </template>
                  </v-list-item>
                </template>
              </v-autocomplete>
            </div>
          </div>
          <div class="d-flex mt-5 align-center" :class="[getButtonsClass]">
            <template v-if="!csvLoaded && !csvLoadError">
              <v-btn
                color="primary"
                :ripple="false"
                variant="text"
                @click="$emit('dismissed')"
                :text="$t('general.cancel')"
              />

              <v-btn
                rounded
                color="primary"
                data-testid="modal-action-button"
                size="large"
                :disabled="actionNotAllowed"
                @click="handleFileImport()"
                :text="$t(`modals.importUrlGroupAllowBlockListFromCsv.actionButton`)"
              />
            </template>
            <v-btn
              v-else
              rounded
              class="close-btn"
              color="primary"
              data-testid="modal-action-button"
              size="large"
              @click="$emit('dismissed')"
            >
              {{ $t("general.close") }}
            </v-btn>
          </div>
        </v-form>
      </template>
    </div>
    <div
      v-else-if="csvLoadError || invalidRecords"
      class="info-section d-flex w-100 flex-column justify-space-between text-center"
    >
      <div class="csv-upload-report error-border-solid d-flex w-100 pl-4 pt-4 pr-4 pb-4">
        <v-icon class="file-icon mr-2" size="70" icon="$dataNoHexagon" />
        <div class="d-flex flex-column align-start">
          <div class="subtitle2">{{ file?.name }}</div>
          <div class="body3 text-indigo-medium mb-2">{{ humanizeFileSize(file?.size) }}</div>
          <div class="d-flex">
            <div class="d-flex align-center">
              <template v-if="invalidRecords || uploadErrorMessage">
                <v-icon icon="$warning" color="red-faint" size="18" class="mr-1" />
                <span v-if="!uploadErrorMessage" class="body2 text-red-dark">
                  {{
                    successfullyUploaded
                      ? $t("modals.importUrlGroupAllowBlockListFromCsv.recordsFailed", {
                          n: invalidRecords,
                          m: successfullyUploaded + invalidRecords,
                        })
                      : $t("model.importUrlGroupAllowBlockListFromCsv.noValidUrls")
                  }}
                </span>
                <span v-else class="body2 text-red-dark">{{ uploadErrorMessage }}</span>
              </template>
              <template v-else>
                <v-icon icon="icon-warning-xs" color="red-faint" size="18" class="mr-1" />
                <span class="body2 text-red-dark">
                  {{ $t(`modals.allowBlockListSwg.uploadFailed`) }}
                </span>
              </template>
            </div>
            <div class="pl-2">
              <a data-testid="retry-link" class="coro-link" @click="handleFileImport(true)">
                {{ $t("general.tryAgain") }}
              </a>
            </div>
          </div>
        </div>
      </div>
      <v-btn
        rounded
        class="close-btn align-self-end"
        color="primary"
        data-testid="modal-action-button"
        size="large"
        @click="$emit('dismissed')"
      >
        {{ $t("general.close") }}
      </v-btn>
    </div>
    <div v-else class="info-section d-flex w-100 flex-column justify-space-between">
      <div class="csv-upload-report d-flex w-100 pl-4 pt-4 pr-4 pb-4">
        <v-icon class="file-icon mr-2" size="70" icon="$dataNoHexagon" />
        <div class="d-flex flex-column align-start">
          <div class="subtitle2">{{ file?.name }}</div>
          <div class="body3 text-indigo-medium mb-2">{{ humanizeFileSize(file?.size) }}</div>
          <div class="d-flex align-center">
            <v-icon size="24" icon="$success" class="mr-1" color="green-dark" />
            <span class="body2 text-green-dark">
              {{ successfullyUploaded }}
              {{ $t("modals.importUrlGroupAllowBlockListFromCsv.recordsUploaded") }}
            </span>
          </div>
        </div>
      </div>
      <v-btn
        rounded
        class="close-btn align-self-end"
        color="primary"
        data-testid="modal-action-button"
        size="large"
        @click="$emit('dismissed')"
      >
        {{ $t("general.close") }}
      </v-btn>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useSWGStore } from "@/_store/network/swg/secure-web-gateway.module.ts";
import { onlySpaces } from "@coronet/cui";
import { DataSizeUnit } from "@/constants/general";
import { SubscriptionModule } from "@/constants/workplaces";
import DraggableFileUploader from "@/components/DraggableFileUploader.vue";
import { useImportCsvModal } from "@/composables/useImportCsvModal";
import { storeToRefs } from "pinia";
import { type DeviceLabel, useDevicesSettingsStore } from "@/_store/devices-settings.module.ts";
import { humanizeFileSize } from "@coronet/cui";
import { isWorkspaceFrozenOrActionRestricted } from "@/_helpers/permissions.ts";
import { RolePermissionsScope } from "@/constants/roles.ts";

export default defineComponent({
  components: { DraggableFileUploader },
  props: {
    config: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const { getSwgExampleFile, importToSwgUrlList, checkSwgUrlGroupNameUnique } = useSWGStore();
    const devicesSettingsStore = useDevicesSettingsStore();
    const { labels } = storeToRefs(devicesSettingsStore);
    const { t } = useI18n();
    const {
      file,
      csvLoaded,
      csvLoading,
      csvLoadError,
      successfullyUploaded,
      invalidRecords,
      errorMessage: uploadErrorMessage,
      handleFileUpload,
      handleImport,
    } = useImportCsvModal();

    const valid = ref(true);
    const form = ref();
    const localValue = ref({
      nickname: "",
      labels: [],
    });
    const validationTriggered = ref(false);
    const nicknameAsyncError = ref("");

    const validateNickname = (value: string) => {
      if (!value || onlySpaces(value)) {
        return t("validations.required");
      }
      if (value.length > 100) {
        return t("validations.maxNCharacters", { n: 100 });
      }
      return true;
    };

    const validateLabels = (value: DeviceLabel[]) => {
      if (!value.length) {
        return t(
          `validations.deviceLabelRequired.${props.config.item.allowed ? "allowList" : "blockList"}`
        );
      }
      return true;
    };

    // Updated to swg module instead of network
    const actionNotAllowed = computed(() =>
      isWorkspaceFrozenOrActionRestricted(RolePermissionsScope.PROTECTION, SubscriptionModule.SWG)
    );

    const nicknameError = computed(() => {
      if (nicknameAsyncError.value) return nicknameAsyncError.value;
      if (!validationTriggered.value) return "";
      const result = validateNickname(localValue.value.nickname);
      return result === true ? "" : result;
    });

    const labelError = computed(() => {
      if (!validationTriggered.value) return "";
      const result = validateLabels(localValue.value.labels);
      return result === true ? "" : result;
    });

    const isFormValid = computed(() => {
      return nicknameError.value === "" && labelError.value === "";
    });

    const clearNicknameError = () => {
      if (validationTriggered.value) {
        validationTriggered.value = false;
      }
      nicknameAsyncError.value = "";
    };

    const clearLabelError = () => {
      if (validationTriggered.value) {
        validationTriggered.value = false;
      }
    };

    const handleFileImport = async (reTry?: boolean) => {
      if (!file.value || !reTry) {
        validationTriggered.value = true;

        if (!file.value || !!errorMessage.value || !isFormValid.value) {
          return;
        }

        const isUnique = await checkSwgUrlGroupNameUnique(localValue.value.nickname);
        if (!isUnique) {
          nicknameAsyncError.value = t("validations.urlGroupNameAlreadyExists");
          return;
        }
      }
      const payload = {
        nickname: localValue.value.nickname,
        allowed: props.config.item.allowed,
        labels: localValue.value.labels.map((singleLabel: DeviceLabel) => singleLabel.id).join(","),
      };
      await handleImport({ file: file.value, ...payload }, importToSwgUrlList);
    };

    const errorMessage = computed(() => {
      if (validationTriggered.value) {
        if (!file.value) {
          return t("modals.allowBlockListSwg.fileMissing");
        }

        if (file.value.type !== "text/plain") {
          return t("modals.allowBlockListSwg.incorrectFileExtension");
        }

        if (file.value.size > DataSizeUnit.MB) {
          return t("modals.allowBlockListSwg.fileSizeError");
        }
      }
      return "";
    });

    const getButtonsClass = computed(() => {
      return !csvLoaded.value && !csvLoadError.value ? "justify-end" : "justify-center";
    });

    return {
      handleFileUpload,
      form,
      valid,
      file,
      localValue,
      actionNotAllowed,
      errorMessage,
      csvLoaded,
      csvLoadError,
      csvLoading,
      handleFileImport,
      getSwgExampleFile,
      getButtonsClass,
      labels,
      humanizeFileSize,
      nicknameError,
      labelError,
      clearNicknameError,
      clearLabelError,
      successfullyUploaded,
      invalidRecords,
      uploadErrorMessage,
    };
  },
});
</script>

<style lang="scss" scoped>
.import-url-group-modal,
.info-section {
  min-height: 300px;
}

.success-icon:before {
  color: rgb(var(--v-theme-green-base));
}

.color-preview {
  width: 16px;
  height: 16px;
  border-radius: 3px;
}

.close-btn {
  margin-bottom: 24px;
}

.error-border {
  border: dashed 1px rgb(var(--v-theme-red-dark));
}

.csv-upload-report {
  border: solid 1px rgb(var(--v-theme-indigo-pale));
  border-radius: 8px;
  &.error-border-solid {
    border: solid 2px rgb(var(--v-theme-red-dark));
  }
}

.file-icon {
  background-color: rgb(var(--v-theme-indigo-faint));
  border-radius: 8px;
}

.caption {
  color: rgb(var(--v-theme-primary));
}

:deep(.icon-success-xs) {
  &:before {
    color: rgb(var(--v-theme-green-dark));
  }
}
:deep(.labels-autocomplete),
:deep(.nickname-input) {
  .v-label.v-field-label {
    color: rgb(var(--v-theme-indigo-medium));
    opacity: 1;
  }
}
</style>
